(() => {
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/,/g, '.');
  const category = title => {
    const text = normalize(title);
    if (text.includes('respaldo')) return /fundas? (de|para) respaldo/.test(text) ? 'cover' : 'headboard';
    if (text.includes('piecera')) return 'runner';
    if (text.includes('cojin') || text.includes('almohada')) return 'cushion';
    return null;
  };
  const size = options => {
    for (const option of options || []) {
      const text = normalize(option).replace(/\s+/g, ' ').trim();
      if (/super\s*king/.test(text)) return 'super-king';
      if (/\bking\b/.test(text)) return 'king';
      if (/\bqueen\b/.test(text)) return 'queen';
      if (/\bfull\b/.test(text)) return 'full';
      if (/\b1\s*\.\s*5\s*plaza/.test(text)) return '1.5';
      if (/\b2\s*plaza/.test(text)) return '2';
      if (/\b1\s*plaza/.test(text)) return '1';
    }
    return null;
  };
  const family = title => normalize(title).match(/\b(pestana|amarra|fuelle|elara|olivia|verona|aurora)\b/)?.[1] || null;
  const appearance = (title, options = []) => {
    const text = normalize([title, ...options].join(' ')).replace(/grisclaro/g, 'gris claro').replace(/grisoscuro/g, 'gris oscuro');
    const colors = [...new Set(text.match(/\b(gris medio|gris claro|gris oscuro|azul marino|verde bosque|verde menta|rosa viejo|taupe|beige|crudo|arena|sand|blanco|cafe|grafito|burdeo|negro|terracota)\b/g) || [])].sort();
    if (!colors.length) return null;
    const design = /\brayas?\b/.test(text) ? 'rayas' : 'liso';
    return design + ':' + colors.join('|');
  };
  const alternativeCover = (source, product, variant) => {
    const sourceFamily = family(source.title), targetFamily = family(product.title);
    if (!sourceFamily || sourceFamily !== targetFamily) return false;
    const currentLook = appearance(source.title, source.options);
    const candidateLook = appearance(product.title, variant.options);
    return !!currentLook && !!candidateLook && currentLook !== candidateLook;
  };
  const select = (catalog, items, currentProduct, sourceId, options = {}) => {
    const limit = options.limit || 6;
    const source = items.find(item => String(item.id) === String(sourceId)) || items[0];
    if (!source) return [];
    const sourceCategory = category(source.title), sourceSize = size(source.options);
    const excluded = new Set(items.map(item => String(item.productId)));
    excluded.add(String(currentProduct));
    const priority = sourceCategory === 'headboard' ? ['cover', 'runner', 'cushion'] : sourceCategory === 'cover' ? ['headboard', 'runner', 'cover', 'cushion'] : ['cover', 'runner', 'cushion', 'headboard'];
    const buckets = Object.fromEntries(priority.map(key => [key, []]));
    const seen = new Set();
    for (const product of catalog) {
      const type = category(product.title);
      if (!buckets[type] || excluded.has(String(product.id)) || seen.has(String(product.id))) continue;
      seen.add(String(product.id));
      const variants = product.variants.filter(variant => variant.available && (type === 'cushion' || (sourceSize && size(variant.options) === sourceSize)) && (type !== 'cover' || alternativeCover(source, product, variant)));
      if (!variants.length) continue;
      const variant = type === 'cushion' ? variants.find(v => (v.options || []).some(option => normalize(option) === 'con relleno')) || variants[0] : variants[0];
      buckets[type].push({ product: {...product, variants}, variant });
    }
    if (normalize(source.title).includes('lino')) {
      for (const bucket of Object.values(buckets)) bucket.sort((a, b) => Number(normalize(b.product.title).includes('lino')) - Number(normalize(a.product.title).includes('lino')));
    }
    const result = [];
    while (result.length < limit && priority.some(key => buckets[key].length)) {
      for (const key of priority) {
        if (buckets[key].length && result.length < limit) result.push(buckets[key].shift());
      }
    }
    return result;
  };
  let catalogPromise;
  const loadCatalog = () => {
    if (!catalogPromise) catalogPromise = fetch((window.Shopify?.routes?.root || '/') + '?section_id=mh-cross-sell-catalog', {credentials:'same-origin'}).then(response => {
      if (!response.ok) throw new Error('Catalog unavailable');
      return response.text();
    }).then(html => JSON.parse(new DOMParser().parseFromString(html, 'text/html').querySelector('[data-mh-cross-catalog]').textContent)).catch(error => {catalogPromise=null;throw error;});
    return catalogPromise;
  };
  window.MHCrossSell = {select, size, category, loadCatalog};
  class MhCartRecommendations extends HTMLElement {
    static select = select;
    connectedCallback() {
      this.state = JSON.parse(this.querySelector('[data-mh-cross-state]').textContent);
      this.drawer = this.closest('cart-drawer');
      this.addEventListener('click', event => { const button = event.target.closest('[data-mh-add]'); if (button) this.add(button); });
      this.openListener = () => this.load();
      this.drawer.addEventListener('mh:cart-open', this.openListener);
      if (this.drawer.classList.contains('active')) this.load();
    }
    disconnectedCallback() { this.drawer.removeEventListener('mh:cart-open', this.openListener); }
    async load() {
      if (this.loading || !this.state.items.length) return;
      this.loading = true;
      try {
        const catalog = await loadCatalog();
        if (!this.isConnected) return;
        this.sourceId = this.drawer.mhRecommendationSource;
        const selected = select(catalog, this.state.items, this.dataset.currentProduct, this.sourceId);
        this.render(selected);
      } catch { this.hidden = true; }
      finally { this.loading = false; }
    }
    render(selected) {
      const list = this.querySelector('.mh-cross__list');
      list.replaceChildren();
      for (const { product, variant } of selected) {
        const card = document.createElement('article'); card.className = 'mh-cross__card'; card.setAttribute('role', 'listitem'); card.dataset.productId = product.id; card.dataset.variantId = variant.id;
        const url = product.url + '?variant=' + variant.id;
        const imageLink = document.createElement('a'); imageLink.className = 'mh-cross__image-link'; imageLink.href = url; imageLink.tabIndex = -1; imageLink.setAttribute('aria-hidden', 'true');
        const image = document.createElement('img'); image.className = 'mh-cross__image'; image.src = product.image; image.alt = ''; image.width = 118; image.height = 128; image.loading = 'lazy'; imageLink.append(image);
        const title = document.createElement('a'); title.className = 'mh-cross__title'; title.href = url; title.textContent = product.title;
        const option = document.createElement('span'); option.className = 'mh-cross__variant'; option.textContent = variant.title === 'Default Title' ? '' : variant.title;
        const price = document.createElement('div'); price.className = 'mh-cross__price'; price.textContent = variant.money;
        if (variant.compare > variant.price) { const previous = document.createElement('s'); previous.textContent = variant.compareMoney; price.append(previous); }
        const button = document.createElement('button'); button.type = 'button'; button.className = 'mh-cross__add'; button.dataset.mhAdd = variant.id; button.textContent = this.state.labels.add; button.setAttribute('aria-label', this.state.labels.add + ': ' + product.title + ', ' + variant.title);
        card.append(imageLink, title, option, price, button); list.append(card);
      }
      this.hidden = !selected.length;
    }
    async add(button) {
      if (this.busy) return;
      this.busy = true;
      this.querySelectorAll('button').forEach(item => { item.disabled = true; });
      button.textContent = this.state.labels.adding;
      const error = this.querySelector('.mh-cross__error'); error.hidden = true;
      try {
        const response = await fetch(this.dataset.root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: [{ id: Number(button.dataset.mhAdd), quantity: 1 }], sections: this.drawer.getSectionsToRender().map(section => section.id), sections_url: window.location.pathname }) });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || this.state.labels.error);
        const anchor = this.sourceId || this.state.items[0].id;
        this.drawer.renderContents(result, anchor);
        this.drawer.querySelector('.drawer__close')?.focus();
      } catch (failure) {
        error.textContent = failure.message || this.state.labels.error; error.hidden = false;
        button.textContent = this.state.labels.add;
        this.querySelectorAll('button').forEach(item => { item.disabled = false; });
      } finally { this.busy = false; }
    }
  }
  if (!customElements.get('mh-cart-recommendations')) customElements.define('mh-cart-recommendations', MhCartRecommendations);
})();


